import { Link } from "react-router-dom";
import LegalPageLayout, { LegalSection } from "./LegalPageLayout";

const CONTACT_EMAIL = "ai.insightpulse@gmail.com";

export default function RefundPolicy({ onClose }) {
  return (
    <LegalPageLayout
      title="Refund & Cancellation Policy"
      subtitle="How cancellations, billing, and refunds work for InsightPulse AI"
      updatedLabel="Last updated: July 2026"
      onClose={onClose}
    >

      <LegalSection title="1. Overview">
        <p>
          InsightPulse AI offers a Free plan and a paid Premium subscription plan. This policy
          explains how cancellations are handled, when Premium access ends, and how refunds and
          billing issues are handled. It should be read together with our{" "}
          <Link to="/terms" className="text-blue-400 hover:text-blue-300 underline">
            Terms & Conditions
          </Link>.
        </p>
      </LegalSection>

      <LegalSection title="2. Cancelling Your Subscription">
        <p>
          You can cancel your Premium subscription at any time from the Subscription page in your
          account. Cancellation is scheduled through our payment processor, Paddle, and does not
          take effect immediately.
        </p>
        <ul className="list-disc list-inside space-y-1.5 pl-1">
          <li>After you confirm cancellation, your subscription is marked as scheduled to cancel.</li>
          <li>You remain on the Premium plan, with full Premium access, until the end of your current billing period.</li>
          <li>
            Once the current billing period ends, the subscription becomes canceled, no further
            charges occur, and your account automatically moves to the Free plan.
          </li>
        </ul>
      </LegalSection>

      <LegalSection title="3. What Happens When You Move to the Free Plan">
        <p>
          After your Premium access ends, your monthly analysis limit, file size limits, and other
          plan capabilities drop to the Free plan tier, and exports will again include InsightPulse
          branding. Your account, datasets, and conversation history are not deleted when you move
          to the Free plan — only your plan-level access changes.
        </p>
      </LegalSection>

      <LegalSection title="4. Refunds">
        <p>
          Payments for InsightPulse AI subscriptions are processed by Paddle, our authorized
          reseller and merchant of record. Refund requests are handled in accordance with
          applicable consumer protection law and Paddle's payment and refund policies.
        </p>
        <p>
          We do not guarantee refunds for partial billing periods, for time you did not use during
          an active subscription, or simply because you changed your mind after a charge. That
          said, we do not treat refunds as impossible in every case — genuine billing errors,
          duplicate charges, or other legitimate payment issues are reviewed on a case-by-case
          basis, and where a refund is appropriate under applicable law or Paddle's policies, we
          will work with you and with Paddle to resolve it.
        </p>
      </LegalSection>

      <LegalSection title="5. Billing Issues, Duplicate Charges & Payment Errors">
        <p>
          If you believe you've been charged incorrectly — for example, a duplicate charge, a
          charge after you believe you already canceled, or any other payment error — please
          contact us as soon as possible at{" "}
          <a href={`mailto:${CONTACT_EMAIL}`} className="text-blue-400 hover:text-blue-300 underline">
            {CONTACT_EMAIL}
          </a>{" "}
          with your account email and, if available, the Paddle receipt or order ID from your
          confirmation email. We'll investigate and respond as soon as reasonably possible, and
          will involve Paddle directly where needed to correct the issue.
        </p>
      </LegalSection>

      <LegalSection title="6. Changes to This Policy">
        <p>
          We may update this Refund & Cancellation Policy from time to time. Material changes will
          be reflected by updating the "Last updated" date above.
        </p>
      </LegalSection>

      <LegalSection title="7. Contact Us">
        <p>
          Questions about billing, cancellations, or refunds can be sent to{" "}
          <a href={`mailto:${CONTACT_EMAIL}`} className="text-blue-400 hover:text-blue-300 underline">
            {CONTACT_EMAIL}
          </a>.
        </p>
      </LegalSection>

    </LegalPageLayout>
  );
}
